import { memo, useCallback, useMemo, useState } from 'react';
import { Button, Colors, Text, Icon } from '@mercator-gcp/grout';
import { useAtom } from 'jotai';
import { isEqual, omit } from 'lodash';
import PopoverMenu from 'components/elements/PopoverMenu';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { isChatCreatedSavedSearch } from 'components/widgets/ProjectInsights/Toolbar/getSavedSearchEditorType';
import SavedSearchDropdown from 'components/widgets/ProjectInsights/Toolbar/SavedSearchDropdown';
import SaveGroupModal from 'components/widgets/ProjectInsights/Toolbar/SaveGroupModal';
import { useSelectedGroupConfig } from 'components/widgets/ProjectInsights/Toolbar/useSelectedGroupConfig';
import {
  useClearRegionSpecificFilters,
  useClearToolbarFilterState,
} from 'hooks/filterToolbar/useClearFilters';
import { useLatestProjectStage } from 'hooks/projectGroupEdit/useProjectGroupForm';
import {
  useGroupConfigurations,
  useInvalidateGroupConfigurations,
} from 'hooks/useGroupConfig';
import { useGroupData } from 'hooks/useGroupData';
import { useProjectMapQueryParams } from 'hooks/useProjectMap';
import { useUpdateGroup } from 'hooks/useUpdateGroup';
import { trackGroupConfigAction } from 'services/analytics/trackGroupConfigAction';
import { useUser } from 'state/auth';
import {
  useDashboardTriggers,
  useDashboardGroupType,
  useDashboardGroupId,
  useDashboard,
  useDashboardExcludePastProjectStage,
  useDashboardPastProjectStage,
  useDashboardGroupName,
  groupAtom,
} from 'state/dashboard';
import { useSelectedPolygonBoundsOnMap } from 'state/map';
import { useGroupIdQuery } from 'state/queryParams';
import { GroupRawResponse } from 'types';
import { GROUP_TYPE } from 'utils/constants';
import { groupRawToGroup } from 'utils/transforms';

const containerStyle = {
  width: '100%',
  marginTop: 'padding_16',
};

const projectSaveBtn = {
  width: '100%',
  paddingTop: (theme: any) => theme.space.eight,
};

const labelStyle = {
  color: Colors.TextQuiet,
  paddingBottom: 'padding_8',
  textAlign: 'center',
};

const buttonContainerStyle = {
  paddingTop: 'padding_8',
};

const splitButtonContainer = {
  display: 'flex',
  width: '100%',
  gap: 0,
  alignItems: 'stretch',
};

const mainButtonStyle = {
  width: '184px',
  flexShrink: 0,
  borderRadius: '4px 0 0 4px',
  backgroundColor: '#027a3b',
  color: Colors.White,
  border: 'none',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '1.23',
  letterSpacing: '-0.13px',
  height: '32px',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#027a3b',
  },
  '&:disabled': {
    backgroundColor: '#027a3b',
    opacity: 0.6,
  },
};

const chevronButtonStyle = {
  borderRadius: '0 4px 4px 0',
  backgroundColor: Colors.ForestGreen900,
  color: Colors.White,
  borderLeft: `1px solid ${Colors.White}`,
  borderTop: 'none',
  borderRight: 'none',
  borderBottom: 'none',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '32px',
  minWidth: 'auto',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: Colors.ForestGreen900,
    padding: '8px 12px',
    borderLeft: `1px solid ${Colors.White}`,
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: 'none',
  },
  '&:disabled': {
    backgroundColor: Colors.ForestGreen900,
    opacity: 0.6,
  },
  '&:focus': {
    backgroundColor: Colors.ForestGreen900,
    padding: '8px 12px',
    borderLeft: `1px solid ${Colors.White}`,
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: 'none',
  },
};

// Since 'status' is always active on a saved search and in saved searches we don't include date fields - we don't need to compare these
const propertiesToOmit = [
  'status',
  'startDate',
  'date_created_start',
  'date_created_end',
];

const SelectExistingGroup = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [savedTriggers] = useDashboardTriggers();
  const [, setGroupType] = useDashboardGroupType();
  const [, setGroupName] = useDashboardGroupName();
  const [dashboardGroupId] = useDashboardGroupId();
  const { saveCities } = useDashboard();
  const [pastProjectStages] = useDashboardPastProjectStage();
  const [excludePastProjectStages] = useDashboardExcludePastProjectStage();
  const { updateGroup } = useUpdateGroup();
  const [isGroupNameModalOpen, setIsGroupNameModalOpen] = useState(false);
  const { user } = useUser();
  const { data } = useGroupConfigurations({ userId: String(user?.id) });
  const selectedGroupConfig = useSelectedGroupConfig();
  const isScoutSearch = isChatCreatedSavedSearch(selectedGroupConfig);
  const { invalidateGroupConfigurations } = useInvalidateGroupConfigurations();
  const [currentGroupState] = useAtom(groupAtom);
  const groupSelected = useGroupData(String(dashboardGroupId));
  const { hasSelectedProjectStages } = useLatestProjectStage();
  const { clearRegionSpecificFilters } = useClearRegionSpecificFilters();
  const { clearToolbarFilterState } = useClearToolbarFilterState();
  const { groupIdQuery, setGroupIdQuery } = useGroupIdQuery();
  const [, setGroupId] = useDashboardGroupId();
  const [selectedPolygonBoundsOnMap, setSelectedPolygonBoundsOnMap] =
    useSelectedPolygonBoundsOnMap();

  const { resetAllQueryParamsForProjectMap } = useProjectMapQueryParams();

  const onClose = () => {
    setIsMenuOpen(false);
  };

  const hasChanges = useMemo(() => {
    if (!groupSelected) {
      // If no group is selected, we want to keep the button enabled
      return true;
    }
    const currentStateOmitted = omit(currentGroupState, propertiesToOmit);
    const selectedStateOmitted = omit(
      groupRawToGroup(groupSelected as GroupRawResponse),
      propertiesToOmit,
    );
    const isSelectedPolygonBoundsEqual = isEqual(
      currentGroupState.polygon_bounds,
      selectedPolygonBoundsOnMap,
    );

    return (
      !isEqual(currentStateOmitted, selectedStateOmitted) || !isSelectedPolygonBoundsEqual
    );
  }, [currentGroupState, groupSelected, propertiesToOmit, selectedPolygonBoundsOnMap]);

  const tooltipText = {
    PROJECT_GROUP: 'Set a Project Stage to save filters as a Saved Search',
  };

  const handleGroupSelection = useCallback(
    (groupId: number) => {
      // Resetting state to default values on city changes
      resetAllQueryParamsForProjectMap();
      setGroupIdQuery(groupId ? String(groupId) : undefined);
      setGroupId(groupId || '');
      setSelectedPolygonBoundsOnMap('');

      if (!groupId) {
        // When clearing the search, persist all filters.
        return;
      }

      // When selecting a group, clear latest project stage so the saved search can load its own
      clearToolbarFilterState();
    },
    [
      resetAllQueryParamsForProjectMap,
      setGroupIdQuery,
      clearRegionSpecificFilters,
      clearToolbarFilterState,
    ],
  );

  const handleUpdateGroup = () => {
    updateGroup.mutate({});
  };

  const handleSaveGroupClick = () => {
    setGroupName('');
    setGroupType(GROUP_TYPE.PROJECT);
    saveCities([savedTriggers[0].cityId], {
      past_project_stage: pastProjectStages.length === 0,
      exclude_past_project_stage: excludePastProjectStages.length === 0,
    });
    trackGroupConfigAction({
      group_id: '0',
      group_type: 'project_group',
      click_action: 'start_group',
    });
    setIsGroupNameModalOpen(true);
  };

  const handleSaveNewSearchClick = () => {
    // Reset group name and type for new search
    setGroupName('');
    setGroupType(GROUP_TYPE.PROJECT);
    saveCities([savedTriggers[0].cityId], {
      past_project_stage: pastProjectStages.length === 0,
      exclude_past_project_stage: excludePastProjectStages.length === 0,
    });
    trackGroupConfigAction({
      group_id: '0',
      group_type: 'project_group',
      click_action: 'start_group',
    });
    setIsGroupNameModalOpen(true);
    setIsMenuOpen(false);
  };

  const handleCloseModal = () => {
    setIsGroupNameModalOpen(false);
  };

  const handleSuccessfulGroupCreated = (groupId: number) => {
    handleCloseModal();
    // The group is created synchronously on the backend, so invalidate every
    // cached saved-search list (map toolbar + dashboard) to guarantee the new
    // group shows up immediately wherever it's rendered.
    invalidateGroupConfigurations(String(user?.id));
    handleGroupSelection(groupId);
  };

  return (
    <FlexColumn sx={containerStyle}>
      <Text.SmallLabel sx={labelStyle}>Load filters from saved searches</Text.SmallLabel>
      <SavedSearchDropdown
        groups={data}
        onSelect={handleGroupSelection}
        selectedGroupId={groupIdQuery}
      />
      {!isScoutSearch && (
        <FlexColumn sx={buttonContainerStyle}>
          {Number(dashboardGroupId) > 0 ? (
            <FlexRow sx={splitButtonContainer}>
              <Button
                sx={mainButtonStyle}
                onClick={handleUpdateGroup}
                disabled={
                  !hasChanges || !hasSelectedProjectStages || updateGroup.isLoading
                }
                isLoading={updateGroup.isLoading}
              >
                Update Search
              </Button>
              <PopoverMenu isOpen={isMenuOpen} onClose={onClose} placement="bottom-end">
                <PopoverMenu.Trigger>
                  <Button
                    variant="unstyled"
                    sx={chevronButtonStyle}
                    disabled={
                      !hasChanges || !hasSelectedProjectStages || updateGroup.isLoading
                    }
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                  >
                    <Icon name={Icon.Name.ChevronDown} fill={Colors.White} size={16} />
                  </Button>
                </PopoverMenu.Trigger>
                <PopoverMenu.Content sx={{ width: '156px' }}>
                  <PopoverMenu.Item
                    onClick={handleSaveNewSearchClick}
                    iconName={Icon.Name.Plus}
                    label="Save new search"
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            </FlexRow>
          ) : (
            <Tooltip
              tooltipText={tooltipText.PROJECT_GROUP}
              placement="bottom"
              fullWidth
              shouldRenderTooltip={!hasSelectedProjectStages}
            >
              <Button
                sx={projectSaveBtn}
                onClick={handleSaveGroupClick}
                disabled={!hasSelectedProjectStages}
              >
                Save search
              </Button>
            </Tooltip>
          )}
        </FlexColumn>
      )}
      <SaveGroupModal
        isOpen={isGroupNameModalOpen}
        onSuccess={handleSuccessfulGroupCreated}
        onClose={handleCloseModal}
      />
    </FlexColumn>
  );
};

export default memo(SelectExistingGroup);
